import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { config } from "@/shared/lib/config";
import { fetcher } from "@/shared/lib/fetcher";

export type RefundRequestListItemDTO = {
  id: string;
  targetType: string;
  studentPackageId: string | null;
  privateLessonPackageId: string | null;
  requestedAt: string;
  reason: string;
  refundAmount: number;
  status: string;
  studentName: string | null;
  packageName: string | null;
};

export type RefundRequestDetailDTO = {
  id: string;
  targetType: string;
  studentPackageId: string | null;
  privateLessonPackageId: string | null;
  requestedBy: string;
  requestedByName: string | null;
  requestedAt: string;
  reason: string;
  refundAmount: number;
  status: string;
  approvedBy: string | null;
  approvedByName: string | null;
  approvedAt: string | null;
  rejectionReason: string | null;
  refundedBy: string | null;
  refundedByName: string | null;
  refundedAt: string | null;
  notes: string | null;
  createdAt: string;
  studentId: string;
  studentName: string;
  memberId: string;
  phoneNumber: string | null;
  email: string | null;
  packageName: string;
  enrolledAt: string;
  packageStatus: string;
  originalPrice: number;
  targetLabel: string;
};

export type RefundsSummaryDTO = {
  totalCount: number;
  requestedCount: number;
  approvedCount: number;
  rejectedCount: number;
  refundedCount: number;
  totalRefundAmountRequested: number;
  totalRefundAmountCompleted: number;
};

export type RefundsSummaryQuery = {
  filters?: Array<{ field: string; op: string; value: unknown }>;
};

export type RefundRequestsPageDTO = {
  data: RefundRequestListItemDTO[];
  meta: { total: number; offset: number; limit: number };
};

export type EligiblePackageDTO = {
  id: string;
  targetType: "package" | "private_lesson_package";
  studentName: string;
  memberId: string;
  packageName: string;
  originalPrice: number;
  value: string;
};

export const refundsApi = {
  eligiblePackages: {
    async list(params: {
      search?: string;
      limit?: number;
      pageParam?: number;
    }): Promise<{ items: EligiblePackageDTO[]; nextPage: number | null }> {
      const url = new URL(`${config.apiUrl}/refunds/eligible-packages`);
      if (params.search) url.searchParams.set("search", params.search);
      if (params.limit) url.searchParams.set("limit", String(params.limit));
      if (params.pageParam)
        url.searchParams.set("pageParam", String(params.pageParam));
      return fetcher.get<{
        items: EligiblePackageDTO[];
        nextPage: number | null;
      }>(url.toString());
    },
    async preload(value: string): Promise<EligiblePackageDTO | null> {
      const url = new URL(`${config.apiUrl}/refunds/eligible-packages/preload`);
      url.searchParams.set("value", value);
      return fetcher.get<EligiblePackageDTO | null>(url.toString());
    },
  },
  async getSummary(query: RefundsSummaryQuery): Promise<RefundsSummaryDTO> {
    const url = new URL(`${config.apiUrl}/refunds/summary`);
    if (query.filters)
      url.searchParams.set("filters", JSON.stringify(query.filters));
    return fetcher.get<RefundsSummaryDTO>(url.toString());
  },

  requests: {
    async create(data: {
      targetType: "package" | "private_lesson_package";
      studentPackageId?: string;
      privateLessonPackageId?: string;
      reason: string;
      refundAmount: number;
    }): Promise<{ id: string }> {
      return fetcher.post<{ id: string }>(
        `${config.apiUrl}/refunds/requests`,
        data,
      );
    },
    async list(query: OffsetPageQueryDTO): Promise<RefundRequestsPageDTO> {
      const url = new URL(`${config.apiUrl}/refunds/requests`);
      url.searchParams.set("limit", String(query.limit ?? 20));
      url.searchParams.set("offset", String(query.offset ?? 0));
      if (query.sort) url.searchParams.set("sort", JSON.stringify(query.sort));
      if (query.filters)
        url.searchParams.set("filters", JSON.stringify(query.filters));
      return fetcher.get<RefundRequestsPageDTO>(url.toString());
    },
    async getById(id: string): Promise<RefundRequestDetailDTO> {
      return fetcher.get<RefundRequestDetailDTO>(
        `${config.apiUrl}/refunds/requests/${id}`,
      );
    },
    async approve(id: string): Promise<{ success: boolean }> {
      return fetcher.post<{ success: boolean }>(
        `${config.apiUrl}/refunds/requests/${id}/approve`,
        {},
      );
    },
    async reject(
      id: string,
      data: { rejectionReason?: string },
    ): Promise<{ success: boolean }> {
      return fetcher.post<{ success: boolean }>(
        `${config.apiUrl}/refunds/requests/${id}/reject`,
        data,
      );
    },
    async execute(id: string): Promise<{ success: boolean }> {
      return fetcher.post<{ success: boolean }>(
        `${config.apiUrl}/refunds/requests/${id}/execute`,
        {},
      );
    },
  },
};
